import { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import {
  FeatureAppDefinition,
  FeatureServices,
  Logger,
} from '@feature-hub/core';
import { HistoryServiceV1 } from '@feature-hub/history-service';
import { ReactFeatureApp } from '@feature-hub/react';
import { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import { ServerRequestV1 } from '@feature-hub/server-request';
import { FaContentServiceV1 } from '@volkswagen-onehub/fa-content-service';
import { FeatureappServiceV1 } from '@volkswagen-onehub/featureapp-service';
import { TrackingManagerV1 } from '@volkswagen-onehub/tracking-service';
import { ThemeProvider } from '@vw-marketing/us-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LaunchDarklyWrapper } from './components/launch-darkly-wrapper/LaunchDarklyWrapper';
import { LayerManagerV27 } from '@volkswagen-onehub/layer-manager';
import { PageInfoServiceV1 } from '@volkswagen-onehub/page-info-service';
import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import { BreadcrumbServiceV1 } from '@volkswagen-onehub/breadcrumb-service';

import { ServiceConfigProviderV1 } from '@volkswagen-onehub/service-config-provider';

import React from 'react';

import { FeatureAppEnvironmentContext } from './hooks/use-feature-app-environment';
import { HistoryContext } from './hooks/use-history';

import {
  FeatureAppMemoryStoreV1,
  featureAppMemoryStoreDefinition,
} from './store/feature-app-memory-store';

import { StoreModel } from './store/types';
import { App } from './app';
import { FeatureServicesContext } from './hooks/use-feature-services';
import { loadInitialDataForSSR } from './store/load-initial-data';
import { createApolloClient } from './store/create-apollo-client';
import { getInitialState } from './store/slices';
import { getQueryParams } from './utils/query-param-helpers';

import { cleanStore } from './store';

declare var __VERSION__: String;

export interface FeatureAppDependencies extends FeatureServices {
  readonly 's2:async-ssr-manager'?: AsyncSsrManagerV1;
  readonly 's2:history': HistoryServiceV1;
  readonly 's2:logger'?: Logger;
  readonly 's2:serialized-state-manager': SerializedStateManagerV1;
  readonly 's2:server-request'?: ServerRequestV1;
  readonly 'breadcrumb-service'?: BreadcrumbServiceV1;
  readonly 'fa-content-service'?: FaContentServiceV1;
  readonly 'featureapp-service': FeatureappServiceV1;
  readonly 'locale-service': LocaleServiceV1;
  readonly 'service-config-provider': ServiceConfigProviderV1;
  readonly 'feature-app-memory-store': FeatureAppMemoryStoreV1<StoreModel>;
  readonly tracking?: TrackingManagerV1;
  readonly 'layer-manager': LayerManagerV27;
  readonly 'page-info-service': PageInfoServiceV1;
}

export type FeatureAppMode = 'standalone';

export interface FeatureAppConfig {
  readonly faHostServerUrl?: string;
  readonly faServicesUrl: string;
  readonly mockIds?: string;
  readonly mocksBaseUrl?: string;
  readonly mode: FeatureAppMode;
  readonly graphQLServerUrl?: string;
  readonly graphQLConfigProviderName?: string;
  readonly useLoggerFeatureService?: boolean;
}

export const featureAppDefinition: FeatureAppDefinition<
  ReactFeatureApp,
  FeatureAppDependencies,
  FeatureAppConfig
> = {
  dependencies: {
    featureServices: {
      's2:history': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'feature-app-memory-store': '^1.0.0',
      'featureapp-service': '^1.3.0',
      'locale-service': '^1.0.0',
      'service-config-provider': '^1.0.0',
    },
  },

  optionalDependencies: {
    featureServices: {
      'page-info-service': '^1.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:logger': '^1.0.0',
      's2:server-request': '^1.0.0',
      tracking: '^1.0.0',
      'fa-content-service': '^1.0.0',
      'breadcrumb-service': '^1.0.0',
      'layer-manager': '^2.3.0',
    },
  },
  ownFeatureServiceDefinitions: [featureAppMemoryStoreDefinition],

  create: (env) => {
    const { featureServices, baseUrl, config } = env;

    const {
      's2:async-ssr-manager': asyncSsrManager,
      's2:history': historyService,
      's2:logger': logger,
      's2:serialized-state-manager': serializedStateManager,
      's2:server-request': serverRequest,
      'feature-app-memory-store': memoryStore,
    } = featureServices;

    // Use logger if available, otherwise fall back to console
    const log = logger || console;

    // Determine if we're on the server (SSR) or client
    // asyncSsrManager only exists during SSR, so if it's undefined, we're on the client
    const isServer = asyncSsrManager !== undefined;
    const logPrefix = isServer ? '[SSR]' : '[Client]';

    const history = asyncSsrManager
      ? historyService.createStaticHistory()
      : historyService.createBrowserHistory();

    if (asyncSsrManager) {
      log.info(`${logPrefix} Server-side rendering detected`);
    }

    const serializedState = serializedStateManager.getSerializedState();

    // Rehydrate from serialized state if available (client-side hydration only)
    // On server, serializedState should be null/undefined - never rehydrate on server
    if (serializedState && !isServer) {
      log.info(`${logPrefix} Rehydrating state from serialized data`);
      const universalState = JSON.parse(serializedState);

      if (!memoryStore.getState()) {
        memoryStore.setState(universalState);
        log.info(`${logPrefix} State rehydrated successfully`);
      } else {
        log.info(`${logPrefix} State already exists, skipping rehydration`);
      }
    }

    // On server, always start with a fresh state to prevent reusing state from previous requests
    if (isServer) 
      cleanStore(memoryStore);

    // Load initial data during SSR if not already loaded
    const existingState = memoryStore.getState();
    let graphQLServerUrl = config?.graphQLServerUrl;

    // Replace https with http during SSR
    /*if (asyncSsrManager && graphQLServerUrl) {
      const originalUrl = graphQLServerUrl;
      graphQLServerUrl = graphQLServerUrl.replace('https://', 'http://');
      if (originalUrl !== graphQLServerUrl) {
        log.info(
          `${logPrefix} Modified GraphQL URL for SSR: ${originalUrl} -> ${graphQLServerUrl}`
        );
      }
    }*/

    // Check if we have valid data (not just empty state with error message)
    // Only skip loading if we have actual data, not just an error state
    const hasValidData =
      existingState &&
      (existingState.qsg.availableModels.length > 0 ||
        existingState.qsg.qsgContent !== null ||
        existingState.qsg.qsgNavigation !== null);

    if (!hasValidData && asyncSsrManager && graphQLServerUrl) {
      log.info(`${logPrefix} Starting initial data load for SSR`);

      const timeoutState = { occurred: false };

      const loadData = async () => {
        try {
          const client = createApolloClient(graphQLServerUrl!);
          const initialState = getInitialState();

          log.info(
            `${logPrefix} Loading initial data from GraphQL server: ${graphQLServerUrl}`
          );
          const loadedState = await loadInitialDataForSSR(
            client,
            history,
            initialState,
            log,
            serverRequest
          );

          if (!timeoutState.occurred) {
            timeoutState.occurred = true;
            memoryStore.setState(loadedState);
            log.info(
              `${logPrefix} Initial data loaded and state set successfully`
            );
          } else
            log.warn(
              `${logPrefix} Data loaded but timeout already occurred, not setting state`
            );
        } catch (error) {
          log.error(`${logPrefix} Error loading initial data:`, error);

          if (!timeoutState.occurred) {
            timeoutState.occurred = true;

            const initialState = getInitialState();
            const errorMessage = `SSR: Failed to load initial data from GraphQL server: ${graphQLServerUrl}. Error: ${error instanceof Error ? error.message : String(error)}`;
            initialState.qsg.notificationMessage = errorMessage;
            memoryStore.setState(initialState);

            log.warn(
              `${logPrefix} Using initial state due to error, SSR will continue`
            );
          } else
            log.warn(
              `${logPrefix} Error occurred but timeout already occurred, not setting state`
            );
        }
      };

      // Wrap in a timeout to ensure promise always resolves
      // This prevents SSR from hanging if GraphQL request hangs
      const loadDataWithTimeout = async () => {
        const timeoutSeconds = 15;
        const timeoutPromise = new Promise<void>((resolve) => {
          setTimeout(() => {
            if (!timeoutState.occurred) {
              timeoutState.occurred = true;

              log.warn(
                `${logPrefix} Data loading timeout, using initial state`
              );

              const initialState = getInitialState();
              const timeoutMessage = `SSR: Data loading timeout from GraphQL server: ${graphQLServerUrl}. The request took longer than ${timeoutSeconds} seconds.`;
              initialState.qsg.notificationMessage = timeoutMessage;
              memoryStore.setState(initialState);
            }

            resolve();
          }, timeoutSeconds * 1000);
        });

        return Promise.race([loadData(), timeoutPromise]);
      };

      const dataPromise = loadDataWithTimeout();
      asyncSsrManager.scheduleRerender(dataPromise);
    } else if (hasValidData && asyncSsrManager) {
      log.info(`${logPrefix} Valid state already exists, skipping data load`);
    } else if (existingState && asyncSsrManager) {
      log.info(
        `${logPrefix} State exists but no valid data (likely error state), will attempt to load`
      );
    }

    serializedStateManager.register(() => {
      const state = memoryStore.getState();
      const serialized = JSON.stringify(state);
      if (asyncSsrManager) {
        log.debug(`${logPrefix} Serializing state for client hydration`);
      }
      return serialized;
    });

    // Log memory state on client before rendering
    if (!isServer) {
      const memoryState = memoryStore.getState();
      const query = getQueryParams(history);

      // Logs for local development only
      // console.group('[CLIENT INIT] Memory State Before Render');
      // console.log(
      //   'Current URL:',
      //   history.location.pathname + history.location.search
      // );
      // /*console.log('Serialized State (raw):', serializedState);
      // if (serializedState) {
      //   try {
      //     const parsedSerializedState = JSON.parse(serializedState);
      //     console.log('Serialized State (parsed):', parsedSerializedState);
      //   } catch (e) {
      //     console.warn('Failed to parse serialized state:', e);
      //   }
      // }*/
      // console.log('Memory Store State:', memoryState);
      // console.log('SSR History (from server):', memoryState?.qsg.ssrHistory);
      // console.log('Current URL:', {
      //   pathname: history.location.pathname,
      //   search: history.location.search,
      //   hash: history.location.hash,
      // });
      // console.log('URL Params:', {
      //   section: query.section,
      //   search: query.search,
      //   page: query.page,
      //   model: query.model,
      //   'model-year': query['model-year'],
      //   'guide-mode': query['guide-mode'],
      // });
      // console.log('About to render all components');
      // console.groupEnd();
    }

    const ThemedApp = () => {
      const { ciGuidelinesRollout } = useFlags()
      
      return (
        <ThemeProvider theme="main" variant={ciGuidelinesRollout ? 'v26' : undefined} >
          <App baseUrl={baseUrl} />
        </ThemeProvider>
      );
    };

    return {
      render: () => (
        <HistoryContext.Provider value={history}>
          <FeatureAppEnvironmentContext.Provider value={env}>
            <FeatureServicesContext.Provider value={featureServices}>
              <LaunchDarklyWrapper baseUrl={baseUrl}>
                <ThemedApp />
              </LaunchDarklyWrapper>
            </FeatureServicesContext.Provider>
          </FeatureAppEnvironmentContext.Provider>
        </HistoryContext.Provider>
      ),
    };
  },
};

export default featureAppDefinition;
